import { FilterCondition } from "@irohana-dev/components";
import { FC, useMemo } from "react";
import { useEnum } from "@/api/services/global/enum";
import { useJobFilterOptions } from "@/api/services/main/job";
import { TSavedConditionShareScope } from "@/api/services/main/saved-condition/dtos/saved-condition.dto";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { TFilterConditionPanelConfig } from "@/components/commons/filter-condition/types";
import { useFilterConditionPanel } from "@/components/commons/filter-condition/use-filter-condition-panel";
import {
  buildPastJobsFilterFields,
  fromFilterValue,
  toFilterValue,
} from "@/features/company/components/company-detail/past-jobs/past-jobs-filter-condition-adapter";
import {
  PAST_JOBS_LOCKED_COLUMN_IDS,
  TJobDisplayColumnSetting,
  TPastJobsFilterForm,
  defaultPastJobsDisplayColumns,
  defaultPastJobsFilterValues,
  getPastJobsDisplayColumnsKey,
  getPastJobsPageType,
  normalizePastJobsDateFields,
  normalizePastJobsMultiSelectFields,
  stripEmptyPastJobsFilterFields,
} from "@/features/company/components/company-detail/past-jobs/past-jobs-shared";
import { FILTER_LABEL_FULL_WIDTH_MAX_LENGTH, FILTER_LABEL_MAX_LENGTH } from "@/features/job/components/job-list/job-filter-shared";
import { truncateWithEllipsis } from "@/utils/string";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { FILTER_CONDITION_PANEL_WIDTH } from "@/components/commons/filter-condition/filter-condition-panel-width";

type Props = {
  isOpen: boolean;
  /** `create` is the フィルタ panel; `edit` is 保存条件を編集, which only saves the condition back. */
  mode?: "create" | "edit";
  onClose: () => void;
  onApplyFilters?: (filters: TPastJobsFilterForm) => void;
  onApplyDisplaySettings?: (columns: TJobDisplayColumnSetting[]) => void;
  initialFilters?: Partial<TPastJobsFilterForm>;
  initialDisplayColumns?: TJobDisplayColumnSetting[];
  /** Present when a saved condition is applied; saving then overwrites it instead of creating one. */
  appliedConditionId?: number | null;
  appliedConditionName?: string;
  /** The applied condition's own sharing settings, so editing one shows its values and not the last edit's. */
  appliedConditionShareScope?: TSavedConditionShareScope;
  appliedConditionIsDefault?: boolean;
  appliedConditionIncludeColumnSettings?: boolean;
  /** The column order/hidden pair stored with the condition, when it was saved with one. */
  appliedConditionColumnSettings?: { order?: string[]; hidden?: string[] } | null;
  /** Reports the just-saved condition so the toolbar can show it as the applied one. */
  onConditionSaved?: (id: number, title: string) => void;
  /** Both the saved-condition scope and the stored columns are per company. */
  companyId?: number;
};

export const PastJobsFilterConditionPanel: FC<Props> = ({ mode = "create", companyId, ...rest }) => {
  const { data: jobStatusOptions } = useEnum("EJobStatus");
  const { jobNameOptions, workplaceOptions } = useJobFilterOptions(companyId ? { companyId } : undefined);

  const config = useMemo<TFilterConditionPanelConfig<TPastJobsFilterForm, TJobDisplayColumnSetting>>(
    () => ({
      pageType: getPastJobsPageType(companyId),
      defaultFilters: defaultPastJobsFilterValues,
      defaultColumns: defaultPastJobsDisplayColumns,
      columnsStorageKey: getPastJobsDisplayColumnsKey(companyId),
      lockedColumnIds: PAST_JOBS_LOCKED_COLUMN_IDS,
      normalizeFilters: (filters) => normalizePastJobsMultiSelectFields(normalizePastJobsDateFields(filters)),
      toFilterValue,
      fromFilterValue,
      stripEmptyFilters: stripEmptyPastJobsFilterFields,
      getDateRange: (filters) => ({ from: filters.endDateFrom, to: filters.endDateTo }),
    }),
    [companyId],
  );

  const panel = useFilterConditionPanel(config, { ...rest, mode });

  const fields = useMemo(
    () =>
      buildPastJobsFilterFields({
        status: (jobStatusOptions ?? []).map((o) => ({ ...o, label: truncateWithEllipsis(o.label, FILTER_LABEL_MAX_LENGTH) })),
        jobName: jobNameOptions.map((o) => ({ ...o, label: truncateWithEllipsis(o.label, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH) })),
        workplace: workplaceOptions.map((o) => ({ ...o, label: truncateWithEllipsis(o.label, FILTER_LABEL_FULL_WIDTH_MAX_LENGTH) })),
      }),
    [jobStatusOptions, jobNameOptions, workplaceOptions],
  );

  return (
    <>
      <FilterCondition
        width={FILTER_CONDITION_PANEL_WIDTH}
        shareColor={filterConditionShareColor}
        isOpen={rest.isOpen}
        mode={panel.displayMode}
        onClose={rest.onClose}
        fields={fields}
        value={panel.value}
        onChange={panel.setValue}
        columns={panel.columnOptions}
        columnOrder={panel.columnOrder}
        hiddenColumns={panel.hiddenColumns}
        lockedColumns={PAST_JOBS_LOCKED_COLUMN_IDS}
        onColumnSettingsChange={panel.handleColumnSettingsChange}
        conditionName={panel.conditionName}
        onConditionNameChange={panel.handleConditionNameChange}
        conditionNameError={panel.conditionNameError}
        appliedConditionId={rest.appliedConditionId ?? undefined}
        shareScope={panel.shareScope}
        onShareScopeChange={panel.setShareScope}
        isDefault={panel.isDefault}
        onIsDefaultChange={panel.setIsDefault}
        includeColumnSettings={panel.includeColumnSettings}
        onIncludeColumnSettingsChange={panel.setIncludeColumnSettings}
        hasSavedColumnSettings={panel.hasSavedColumnSettings}
        onSaveCondition={panel.handleSaveCondition}
        isSaving={panel.isSaving}
        onApply={panel.handleApply}
        onClear={() => panel.hasDataToClear && panel.setIsClearConfirmOpen(true)}
      />
      <ConfirmDeleteModal
        open={panel.isClearConfirmOpen}
        onClose={() => panel.setIsClearConfirmOpen(false)}
        onOk={panel.clearValue}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
